import React, { useCallback } from 'react';
import { NfcCardPermission } from 'types';
import { v4 as uuidv4 } from 'uuid';
import Text from 'components/Text/Text';
import Button from 'components/Button/Button';
import { UUIDCardField } from './components/UUIDCardField';
import { FullNameField } from './components/FullNameField';
import { NfcCardPermissionSelect } from './components/NfcCardPermissionSelect';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './NfcCardsFields.module.scss';

interface IProps {
	settingsId: string;
	isLastBlock: boolean;
	changeUUIDCard: (settingsId: string, value: string) => void;
	changeFullName: (settingsId: string, value: string) => void;
	changeNfcCardPermission: (settingsId: string, value: NfcCardPermission[]) => void;
	deleteNfcCardsBlock: (value: string) => void;
}

export const NfcCardsFields: React.FC<IProps> = ({
	settingsId,
	isLastBlock,
	changeUUIDCard,
	changeFullName,
	changeNfcCardPermission,
	deleteNfcCardsBlock,
}) => {
	const handleChangeUUIDCard = useCallback(
		(value: string) => {
			changeUUIDCard(settingsId, value);
		},
		[settingsId, changeUUIDCard],
	);

	const handleGenerateUUIDCard = useCallback(() => {
		const uniqueUUIDCard = uuidv4();
		changeUUIDCard(settingsId, uniqueUUIDCard);
	}, [settingsId, changeUUIDCard]);

	const handleChangeFullName = useCallback(
		(value: string) => {
			changeFullName(settingsId, value);
		},
		[settingsId, changeFullName],
	);

	const handleChangeNfcCardPermission = useCallback(
		(value: NfcCardPermission[]) => {
			changeNfcCardPermission(settingsId, value);
		},
		[settingsId, changeNfcCardPermission],
	);

	const handleDeleteBlock = useCallback(() => {
		deleteNfcCardsBlock(settingsId);
	}, [settingsId, deleteNfcCardsBlock]);

	return (
		<div className={style.fieldsWrapper}>
			<div className={style.fields}>
				<div className={style.row}>
					<UUIDCardField settingsId={settingsId} onChange={handleChangeUUIDCard} />
					<Button onClick={handleGenerateUUIDCard} theme="primary">
						<Text translationNameSpace="form" translationKey="generateUniqueExternalId" asFragment={true} />
					</Button>
				</div>
				<FullNameField settingsId={settingsId} onChange={handleChangeFullName} />
				<NfcCardPermissionSelect settingsId={settingsId} onChange={handleChangeNfcCardPermission} />
			</div>
			<div className={style.deleteButtonWrapper}>
				<Button
					className={style.deleteButton}
					theme="secondary"
					icon={
						<svg className={style.deleteIcon}>
							<use xlinkHref={`${breadcrumbs}#bin`} />
						</svg>
					}
					onClick={handleDeleteBlock}>
					<Text translationNameSpace="form" translationKey="deleteSettingsButton" asFragment={true} />
				</Button>
			</div>
			{!isLastBlock && <div className={style.divider} />}
		</div>
	);
};
